import { css } from '@emotion/react';
import { useState } from 'react';
import type { BoundFlight, Segment } from 'shared';

import { getFlightStopText } from '../../utils';
import { SasIconButton } from '../SasIcon';
import { theme } from '../theme';

import { FlightRowDetailsPopup } from './FlightRowDetailsPopup';
import { getDayDifference } from './utils';

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: column;
  `,
  flightInfoWrapper: css`
    position: relative;
    display: inline-block;
  `,
  flightInfoButton: css`
    margin-left: ${theme.spacing.sm};
    height: 100%;
  `,
  durationWrapper: css`
    color: ${theme.colors.gray40};
    font-size: ${theme.fontSize.xs};
    font-weight: 400;
    text-align: left;
  `,
  time: (isMultiDay: boolean) => css`
    font-size: ${theme.fontSize.xs};
    font-weight: 700;
    text-align: left;
    margin-bottom: ${theme.spacing.xs};

    svg {
      width: 16px;
      height: 16px;
    }

    @media (min-width: ${theme.breakpoints.mobileL}) {
      font-size: ${theme.fontSize.sm};

      svg {
        width: 20px;
        height: 20px;
      }
    }

    @media (min-width: ${theme.breakpoints.laptop}) {
      font-size: ${isMultiDay ? '0.8125rem' : theme.fontSize.sm};
    }
  `,
};

type Props = {
  flight: BoundFlight | BoundFlight;
  className?: string;
};

export function FlightRowDetails({ flight, className }: Props) {
  const [showFlightInfo, setShowFlightInfo] = useState(false);

  const totalStops = getTotalStops(flight);
  const dayDifference = getDayDifference(flight.startDateTimeInLocal, flight.endDateTimeInLocal);

  return (
    <div css={styles.wrapper} className={className}>
      <div css={styles.time(Boolean(dayDifference))}>
        <div>
          {flight.startTimeInLocal} - {flight.endTimeInLocal}
          {dayDifference === 0 ? '' : `+${dayDifference}`}
          <span css={styles.flightInfoWrapper}>
            <SasIconButton
              css={styles.flightInfoButton}
              tabIndex={1}
              onClick={e => {
                e.stopPropagation();
                setShowFlightInfo(prev => !prev);
              }}
              name="cl--info-circle"
              color="var(--color-blue-2)"
            />

            <FlightRowDetailsPopup
              open={showFlightInfo}
              flight={flight}
              close={e => {
                setShowFlightInfo(false);
                e.stopPropagation();
              }}
            />
          </span>
        </div>
      </div>

      <div css={styles.durationWrapper}>
        {getFlightStopText(totalStops)}, {flight.connectionDuration}
      </div>
    </div>
  );
}

function getTotalStops(flight: BoundFlight | BoundFlight) {
  const viaStops = flight.segments.reduce((viaStops: number, segment: Segment) => {
    if (segment.via) {
      viaStops = viaStops + segment.via.length;
    }
    return viaStops;
  }, 0);
  return flight.stops + viaStops;
}
